/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import {
  Accordion,
  AccordionHeader,
  AccordionPanel,
  Grid,
  Radio,
  SelectField,
  SelectOption,
  Typography2,
} from '@coursera/cds-core';

import type { FilterOption } from 'bundles/enterprise-admin-drawers/components/types';
import ActivityFilterDateClause from 'bundles/enterprise-admin-user-filters/components/ActivityFilterDateClause';
import { EnrolledInCoursesAccordion } from 'bundles/enterprise-admin-user-filters/components/activity-filters/common/Accordions';
import { SubSection } from 'bundles/enterprise-admin-user-filters/components/ui';
import { getSpecializationProgressLevels } from 'bundles/enterprise-admin-user-filters/constants';
import type {
  ActivityFilter,
  SpecializationProgressLevel,
  SpecializationProgressLevelAdditionalFilter,
} from 'bundles/enterprise-admin-user-filters/types';
import { getDays } from 'bundles/enterprise-admin-user-filters/utils';
import { DebouncedTextField, Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type SpecializationInProgressProps = {
  selectedCourses: FilterOption[];
  activityFilter: ActivityFilter;
  setSelectedCourses: (selectedCourses: FilterOption[]) => void;
  onActivityFilterChange: (activityFilter: ActivityFilter) => void;
};

type ProgressInSpecializationProps = {
  activityFilter: ActivityFilter;
  onActivityFilterChange: (activityFilter: ActivityFilter) => void;
};

const ProgressInSpecializationAccordion = ({
  activityFilter,
  onActivityFilterChange,
}: ProgressInSpecializationProps) => {
  const { specializationProgress, lastActivityDate, completionDate } = activityFilter;
  let initialAdditionalFilterType: SpecializationProgressLevelAdditionalFilter = 'NONE';

  if (lastActivityDate) {
    initialAdditionalFilterType = 'RECENT_INACTIVITY';
  } else if (completionDate) {
    initialAdditionalFilterType = 'LAST_COMPLETION_DATE';
  }

  const [additionalFilterType, setAdditionalFilterType] =
    React.useState<SpecializationProgressLevelAdditionalFilter>(initialAdditionalFilterType);

  const handleLevelOfProgressChange = (event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    const levelOfProgress = event.target.value as SpecializationProgressLevel;
    onActivityFilterChange({ ...activityFilter, specializationProgress: levelOfProgress });
  };

  const handleActivityFilterTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalFilterType(event.target.value as SpecializationProgressLevelAdditionalFilter);
  };

  const handleNumberOfDaysSinceLastCompletionChange = (value: string) => {
    onActivityFilterChange({
      ...activityFilter,
      completionDate: {
        type: 'RELATIVE',
        nDaysAgoAfter: Number(value),
      },
    });
  };

  const handleNumberOfDaysInactiveChange = (value: string) => {
    onActivityFilterChange({
      ...activityFilter,
      lastActivityDate: {
        type: 'RELATIVE',
        nDaysAgoBefore: Number(value),
      },
    });
  };

  return (
    <Accordion variant="silent" defaultExpanded>
      <AccordionHeader label={_t('Progress in specialization: Any progress')} />
      <AccordionPanel>
        <Grid container direction="column">
          <Grid item>
            <SelectField
              value={specializationProgress}
              label={_t('Current level of progress in a specialization')}
              placeholder={_t('Any progress (excludes no progress)')}
              onChange={handleLevelOfProgressChange}
              data-testid="progress_in_specialization"
            >
              {getSpecializationProgressLevels().map((level) => (
                <SelectOption key={level.value} selected={level.value === specializationProgress} value={level.value}>
                  {level.label}
                </SelectOption>
              ))}
            </SelectField>
          </Grid>
          <Spacer size={24} />
          <Grid item>
            <Typography2 component="h3" variant="subtitleMedium">
              {_t('Additional Filters')}
            </Typography2>
            <Spacer size={16} />
            <Grid container spacing={32}>
              <Grid item>
                <Radio
                  checked={additionalFilterType === 'NONE'}
                  label={_t('None')}
                  value="NONE"
                  onChange={handleActivityFilterTypeChange}
                  data-testid="none_radio"
                />
              </Grid>
              <Grid item>
                <Radio
                  checked={additionalFilterType === 'LAST_COMPLETION_DATE'}
                  label={_t('Last completion date')}
                  value="LAST_COMPLETION_DATE"
                  onChange={handleActivityFilterTypeChange}
                  data-testid="last_completion_date_radio"
                />
              </Grid>
              <Grid item>
                <Radio
                  checked={additionalFilterType === 'RECENT_INACTIVITY'}
                  label={_t('Recent inactivity')}
                  value="RECENT_INACTIVITY"
                  onChange={handleActivityFilterTypeChange}
                  data-testid="recent_inactivity_radio"
                />
              </Grid>
            </Grid>
          </Grid>
          <Spacer size={24} />
          <Grid item>
            {additionalFilterType === 'LAST_COMPLETION_DATE' && (
              <DebouncedTextField
                type="number"
                defaultValue={String(getDays(completionDate) ?? '')}
                label={_t('Last completion date within (days)')}
                onChange={handleNumberOfDaysSinceLastCompletionChange}
                data-testid="additional_value_last_completed_date"
              />
            )}
            {additionalFilterType === 'RECENT_INACTIVITY' && (
              <DebouncedTextField
                type="number"
                defaultValue={String(getDays(lastActivityDate) ?? '')}
                label={_t('Inactive for last (days)')}
                supportText={_t(
                  'A learner is classified as inactive when they have not engaged in any learning activities: reading, watching videos, or completing assignments'
                )}
                maxLength={16}
                onChange={handleNumberOfDaysInactiveChange}
                data-testid="additional_value_inactive"
              />
            )}
          </Grid>
        </Grid>
      </AccordionPanel>
    </Accordion>
  );
};

const SpecializationInProgressActivityFilter = ({
  selectedCourses,
  activityFilter,
  setSelectedCourses,
  onActivityFilterChange,
}: SpecializationInProgressProps) => (
  <>
    <SubSection>
      <EnrolledInCoursesAccordion
        selectedCourses={selectedCourses}
        onCoursesSelected={setSelectedCourses}
        onlySpecialization
      />
    </SubSection>
    <Spacer size={16} />
    <SubSection>
      <ActivityFilterDateClause activityFilter={activityFilter} onActivityFilterChange={onActivityFilterChange} />
    </SubSection>
    <Spacer size={16} />
    <SubSection>
      <ProgressInSpecializationAccordion
        activityFilter={activityFilter}
        onActivityFilterChange={onActivityFilterChange}
      />
    </SubSection>
  </>
);

export default SpecializationInProgressActivityFilter;
